import { Extension } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { remark } from "remark";
import type { RootContent } from "mdast";
import { plainDocToMarkdown } from "@/lib/markdown/markdownPlainDoc";
import {
  MARKDOWN_PREVIEW_LIST_LINE_REGEX,
  SPARX_VARIABLE_REGEX,
} from "@/lib/markdown/markdownShared";

import {
  MARKDOWN_PREVIEW_BOLD_CLASS,
  MARKDOWN_PREVIEW_HR_TEXT_CLASS,
  MARKDOWN_PREVIEW_ITALIC_CLASS,
  MARKDOWN_PREVIEW_LIST_LINE_CLASS,
  MARKDOWN_PREVIEW_VARIABLE_CLASS,
} from "./markdownPreviewClasses";

const PLUGIN_KEY = new PluginKey("markdownPreviewDecorations");

const BOLD_CLASS = MARKDOWN_PREVIEW_BOLD_CLASS;
const ITALIC_CLASS = MARKDOWN_PREVIEW_ITALIC_CLASS;
const VARIABLE_CLASS = MARKDOWN_PREVIEW_VARIABLE_CLASS;
const HR_CLASS = MARKDOWN_PREVIEW_HR_TEXT_CLASS;
const LIST_LINE_CLASS = MARKDOWN_PREVIEW_LIST_LINE_CLASS;

const THEMATIC_BREAK_LINE = /^[ \t]{0,3}(?:-{3,}|\*{3,}|_{3,})[ \t]*$/;

const BOLD_DELIMITER = "**";
const ITALIC_DELIMITER = "*";

const OPENING_AND_CLOSING_DELIMITERS = 2;

type NodeWithPosition = RootContent & {
  position?: { start: { offset: number }; end: { offset: number } };
  children?: NodeWithPosition[];
};

const visit = (
  node: NodeWithPosition,
  decorations: Decoration[],
  contentStart: number,
): void => {
  const pos = node.position;
  const startOffset = pos?.start?.offset;
  const endOffset = pos?.end?.offset;
  if (typeof startOffset === "number" && typeof endOffset === "number") {
    if (node.type === "strong" || node.type === "emphasis") {
      const delim = node.type === "strong" ? BOLD_DELIMITER : ITALIC_DELIMITER;
      const span = endOffset - startOffset;
      const guardOk = span > delim.length * OPENING_AND_CLOSING_DELIMITERS;
      const from = contentStart + startOffset + delim.length;
      const to = contentStart + endOffset - delim.length;
      if (node.type === "strong" && guardOk) {
        decorations.push(Decoration.inline(from, to, { class: BOLD_CLASS }));
      } else if (node.type === "emphasis" && guardOk) {
        decorations.push(Decoration.inline(from, to, { class: ITALIC_CLASS }));
      }
    }
  }
  const { children } = node;
  if (Array.isArray(children) && children.length > 0) {
    for (const child of children) {
      visit(child, decorations, contentStart);
    }
  }
};

const getDecorations = (doc: Node): Decoration[] => {
  const decorations: Decoration[] = [];
  const fullMd = plainDocToMarkdown(doc);
  if (!fullMd.trim()) {
    return decorations;
  }

  let contentStart = 0;
  let firstTextPos = Infinity;
  doc.descendants((node, pos) => {
    if (node.isText) {
      // pos is the position before this node, i.e. the first character of this text node
      if (pos < firstTextPos) firstTextPos = pos;
    }
  });
  if (firstTextPos !== Infinity) contentStart = firstTextPos;

  try {
    const tree = remark().parse(fullMd);
    const root = tree as { children?: NodeWithPosition[] };
    const rootChildren = root.children;
    if (Array.isArray(rootChildren) && rootChildren.length > 0) {
      for (const child of rootChildren) {
        visit(child, decorations, contentStart);
      }
    }
  } catch {
    // If remark throws (e.g. invalid markdown), return no bold/italic decorations.
  }

  // Variables: not part of CommonMark; one simple regex over the string.
  SPARX_VARIABLE_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SPARX_VARIABLE_REGEX.exec(fullMd)) !== null) {
    const from = contentStart + match.index;
    const to = from + (match[0]?.length ?? 0);
    decorations.push(Decoration.inline(from, to, { class: VARIABLE_CLASS }));
  }

  // Thematic-break lines: ---, ***, ___ on their own line (visual as HR in raw markdown mode).
  doc.descendants((node, pos) => {
    if (
      node.isText &&
      node.text != null &&
      THEMATIC_BREAK_LINE.test(node.text)
    ) {
      const from = pos;
      const to = pos + node.nodeSize;
      decorations.push(Decoration.inline(from, to, { class: HR_CLASS }));
    }
  });

  // List lines: - item or 1. item (visual hint in raw markdown mode).
  doc.descendants((node, pos) => {
    if (!node.isText || node.text == null) return;
    const lines = node.text.split("\n");
    let offset = 0;
    for (const line of lines) {
      if (MARKDOWN_PREVIEW_LIST_LINE_REGEX.test(line)) {
        const from = pos + offset;
        const to = from + line.length;
        decorations.push(
          Decoration.inline(from, to, { class: LIST_LINE_CLASS }),
        );
      }
      offset += line.length + 1;
    }
  });

  return decorations;
};

export const MarkdownPreviewDecorationsExtension = Extension.create({
  name: "markdownPreviewDecorations",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: PLUGIN_KEY,
        state: {
          init(_, state) {
            return DecorationSet.create(state.doc, getDecorations(state.doc));
          },
          apply(tr, set, _oldState, newState) {
            if (tr.docChanged) {
              return DecorationSet.create(
                newState.doc,
                getDecorations(newState.doc),
              );
            }
            return set.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            const set = this.getState(state);
            return set ?? DecorationSet.create(state.doc, []);
          },
        },
      }),
    ];
  },
});
